// Mock data for MedQuore website
export const siteContent = {
  en: {
    nav: {
      home: "Home",
      services: {
        title: "Services",
        submenu: [
          { id: "quality-assurance", title: "Quality Assurance", href: "/services/quality-assurance" },
          { id: "supplier-audit", title: "Global Supplier Audit Support", href: "/services/supplier-audit" },
          { id: "mock-audits", title: "Mock Audits & Inspections", href: "/services/mock-audits" },
          { id: "gap-assessments", title: "Gap Assessments", href: "/services/gap-assessments" },
          { id: "training-leadership", title: "Training & Leadership", href: "/services/training-leadership" },
          { id: "talent-acquisition", title: "Talent Acquisition & Marketplace", href: "/services/talent-acquisition" }
        ]
      },
      about: {
        title: "About",
        submenu: [
          { id: "company", title: "Our Company", href: "/about/company" },
          { id: "team", title: "Leadership Team", href: "/about/team" },
          { id: "values", title: "Values & Mission", href: "/about/values" }
        ]
      },
      insights: {
        title: "Insights",
        submenu: [
          { id: "blog", title: "Blog", href: "/insights/blog" },
          { id: "resources", title: "Resources", href: "/insights/resources" },
          { id: "newsletter", title: "Newsletter", href: "/insights/newsletter" }
        ]
      },
      contact: "Contact",
      bookConsultation: "Get Started"
    },
    hero: {
      headline: "Quality & Regulatory Excellence for Medical Devices and IVDs",
      subheading: "Design, implement, and audit audit-ready QMS across US, EU, and MENA.",
      bullets: [
        "ISO 13485 Lead Auditors & RA experts",
        "EU MDR compliance expertise",
        "From strategy to hands-on execution"
      ],
      primaryCTA: "Book a Consultation",
      secondaryCTA: "Download Capabilities Deck"
    },
    howWeWork: {
      title: "How We Work",
      subtitle: "Our proven methodology ensures efficient delivery and sustainable results",
      steps: [
        { step: "01", title: "Assessment", desc: "Comprehensive gap analysis and regulatory landscape review" },
        { step: "02", title: "Strategy", desc: "Tailored implementation roadmap with clear milestones" },
        { step: "03", title: "Execution", desc: "Hands-on implementation with expert guidance" },
        { step: "04", title: "Validation", desc: "Audit readiness verification and continuous support" }
      ]
    },
    clientSuccess: {
      title: "Trusted by Industry Leaders",
      subtitle: "See how MedQuore has helped medical device and IVD companies achieve regulatory excellence",
      testimonials: [
        {
          quote: "MedQuore transformed our QMS implementation from a 18-month timeline to 8 months with complete audit readiness.",
          author: "Sarah Chen",
          role: "VP Quality Assurance",
          company: "MedDevice Innovations"
        },
        {
          quote: "Their MDR expertise was instrumental in our successful EU market entry. Professional, thorough, and results-driven.",
          author: "Dr. Michael Rodriguez",
          role: "Regulatory Affairs Director", 
          company: "BioTech Solutions"
        },
        {
          quote: "The fractional QA leadership provided exactly what our startup needed - senior expertise without full-time commitment.",
          author: "Lisa Thompson",
          role: "CEO & Founder",
          company: "NextGen Medical"
        }
      ],
      caseStudies: [
        {
          id: "startup-qms",
          title: "Startup QMS Implementation",
          challenge: "Early-stage MedTech startup needed complete QMS from scratch for Series B funding",
          intervention: "Implemented ISO 13485 compliant QMS with integrated risk management and design controls",
          results: ["Achieved ISO 13485 certification in 6 months", "Successfully closed $15M Series B", "Zero non-conformities in initial audit"],
          industry: "Medical Devices",
          stage: "Startup"
        },
        {
          id: "eu-mdr-transition",
          title: "EU MDR Transition for IVD Company",
          challenge: "Established IVD manufacturer faced EU MDR compliance deadline with legacy documentation",
          intervention: "Comprehensive gap analysis, documentation overhaul, and regulatory strategy development",
          results: ["Successful MDR compliance 3 months ahead of deadline", "Maintained EU market access", "Streamlined future regulatory processes"],
          industry: "IVD",
          stage: "Scale-up"
        }
      ]
    },
    finalCTA: {
      title: "Ready to Achieve Audit Readiness?",
      subtitle: "Join leading medical device companies that trust MedQuore for their regulatory excellence journey.",
      description: "Let's discuss how our expertise can accelerate your compliance journey and reduce time-to-market.",
      primaryCTA: "Schedule a Consultation",
      secondaryCTA: "Download Capabilities Deck",
      trustIndicators: [
        "No commitment required",
        "Expert recommendations included"
      ]
    },
    services: [
      {
        id: "quality-assurance",
        title: "Quality Assurance",
        description: "Product quality systems, risk management, process validation, continuous improvement",
        icon: "Shield",
        features: [
          "Quality System creation and implementation",
          "eQMS implementation & migration",
          "Internal Process Audit",
          "ISO 13485, ISO 9001 & ISO 14971 Alignment",
          "Process Validation & Control", 
          "Risk-Based Quality Planning",
          "Continuous Improvement Systems"
        ],
        detailedDescription: "End-to-end quality assurance support ensuring compliance with ISO standards, risk-based planning, and continuous improvement frameworks across the medical device lifecycle.",
        benefits: [
          "Strengthened compliance foundation",
          "Reduced deviations and recalls",
          "Enhanced operational efficiency",
          "Sustainable quality culture"
        ]
      },
      {
        id: "supplier-audit",
        title: "Global Supplier Audit Support",
        description: "Worldwide supplier audits, qualification, CAPA monitoring, risk-based evaluation",
        icon: "Globe",
        features: [
          "Supplier Qualification & Monitoring",
          "On-Site and Remote Audits",
          "Supplier CAPA Follow-Up",
          "Risk-Based Supplier Evaluation"
        ],
        detailedDescription: "Comprehensive global supplier audit services to ensure quality, compliance, and performance throughout your supply chain.",
        benefits: [
          "Stronger global supply chain integrity",
          "Improved supplier performance",
          "Reduced compliance risks",
          "Cost-effective international coverage"
        ]
      },
      {
        id: "mock-audits",
        title: "Mock Audits & Inspections",
        description: "EMA, Notified Body readiness, audit simulations, corrective action planning",
        icon: "Search",
        features: [
          "Audit & Inspection Readiness (EMA, NB)",
          "End-to-End Mock Audits",
          "Documentation & Evidence Review",
          "Corrective Action Planning"
        ],
        detailedDescription: "Realistic simulations and preparation programs designed to help organizations perform confidently during regulatory audits and inspections.",
        benefits: [
          "Minimized regulatory surprises",
          "Improved inspection outcomes",
          "Reduced non-conformance findings",
          "Increased team confidence"
        ]
      },
      {
        id: "gap-assessments",
        title: "Gap Assessments",
        description: "Compliance evaluations, remediation planning, benchmarking, risk mitigation",
        icon: "Target",
        features: [
          "Systematic Gap Analysis",
          "Prioritized Remediation Plans",
          "Compliance Benchmarking",
          "Risk Mitigation Planning"
        ],
        detailedDescription: "In-depth assessments to identify compliance and system gaps, with actionable remediation strategies for regulatory readiness.",
        benefits: [
          "Faster issue detection",
          "Proactive compliance strategies",
          "Streamlined corrective actions",
          "Audit-ready documentation"
        ]
      },
      {
        id: "training-leadership",
        title: "Training & Leadership",
        description: "QA/RA training, fractional leadership, team development, knowledge transfer",
        icon: "Users",
        features: [
          "Customized Training Programs",
          "Fractional QA/RA Leadership",
          "Team Development & Mentoring",
          "Knowledge Transfer Programs"
        ],
        detailedDescription: "Tailored programs to strengthen internal QA/RA capabilities, with fractional leadership support and sustainable knowledge transfer.",
        benefits: [
          "Stronger in-house capabilities",
          "Cost-effective leadership expertise",
          "Retained organizational knowledge",
          "Continuous leadership development"
        ]
      },
      {
        id: "talent-acquisition",
        title: "Talent Acquisition & Marketplace",
        description: "Global talent sourcing, vetted QA/RA experts, flexible resourcing, on-demand marketplace",
        icon: "Users",
        features: [
          "Global Talent Sourcing",
          "Pre-Vetted QA/RA Experts",
          "Flexible Resourcing Models",
          "On-Demand Expertise Marketplace"
        ],
        detailedDescription: "Connecting medical device companies with specialized QA/RA professionals worldwide through a flexible and scalable marketplace.",
        benefits: [
          "Faster recruitment timelines",
          "Access to niche expertise",
          "Scalable resourcing solutions",
          "Reduced hiring risks"
        ]
      }
    ],
    stats: [
      { value: "95%", label: "Faster Time-to-Audit Readiness" },
      { value: "50+", label: "Successful Implementations" },
      { value: "3", label: "Regulatory Regions" },
      { value: "15+", label: "Years Combined Experience" }
    ],
    blogPosts: [
      {
        id: "1",
        title: "ISO 13485:2016 Migration: A Strategic Approach for Medical Device Companies",
        excerpt: "Learn the essential steps for successful ISO 13485 migration and avoid common pitfalls.",
        category: "Quality",
        readTime: "8 min read",
        date: "2024-01-15",
        author: "Dr. Sarah Johnson",
        image: "https://customer-assets.emergentagent.com/job_medquore-preview/artifacts/5fvrjtna_68b01b046850d0befdf00908_b502ce60-a6ac-45a0-ac9b-3d66afde0a43.avif"
      },
      {
        id: "2", 
        title: "EU MDR Implementation: Lessons from the First Two Years",
        excerpt: "Key insights and best practices from early MDR adopters in the medical device industry.",
        category: "Regulatory",
        readTime: "12 min read",
        date: "2024-01-10",
        author: "Michael Chen",
        image: "https://customer-assets.emergentagent.com/job_medquore-preview/artifacts/vhig9642_68b01b04255eec224fb2df00_b1078fc3-c4cf-44b8-8ec9-c2cac3245d72.avif"
      },
      {
        id: "3",
        title: "Building Audit-Ready Documentation: A QMS Perspective",
        excerpt: "Essential documentation strategies that ensure audit readiness from day one.",
        category: "Quality",
        readTime: "6 min read", 
        date: "2024-01-05",
        author: "Lisa Rodriguez",
        image: "https://customer-assets.emergentagent.com/job_medquore-preview/artifacts/hlibb0pt_68b01b048a8f30b5906b8b65_848b5264-9c1d-4313-a4e2-f099cd7c11b4.avif"
      }
    ],
    team: [
      {
        name: "Dr. Sarah Johnson",
        role: "Founder & Principal Consultant",
        bio: "15+ years in medical device quality and regulatory affairs. Former FDA reviewer and ISO 13485 Lead Auditor.",
        credentials: ["Ph.D. Biomedical Engineering", "ISO 13485 Lead Auditor", "RAC Certification"],
        expertise: ["ISO 13485", "FDA Regulations", "Quality Systems"]
      },
      {
        name: "Michael Chen",
        role: "Senior Regulatory Affairs Consultant", 
        bio: "Regulatory strategy expert with extensive EU MDR and FDA experience across multiple device classifications.",
        credentials: ["M.S. Regulatory Science", "RAC Certification", "10+ years industry experience"],
        expertise: ["EU MDR/IVDR", "FDA Submissions", "Regulatory Strategy"]
      }
    ],
    legal: {
      privacy: {
        title: "Privacy Policy",
        lastUpdated: "January 2024",
        sections: [
          {
            title: "Information We Collect",
            content: "We collect information you provide directly to us, such as when you fill out a form, request information, or contact us. This may include your name, email address, phone number, company information, and any messages you send to us."
          },
          {
            title: "How We Use Your Information",
            content: "We use the information we collect to provide, maintain, and improve our services, respond to your inquiries, send you information about our services, and comply with legal obligations."
          },
          {
            title: "Information Sharing",
            content: "We do not sell, trade, or otherwise transfer your personal information to third parties except as described in this policy. We may share information with trusted service providers who assist us in operating our website and conducting our business."
          },
          {
            title: "Data Security",
            content: "We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction."
          },
          {
            title: "Contact Us",
            content: "If you have any questions about this Privacy Policy, please contact us at contact@medquore.com."
          }
        ]
      },
      terms: {
        title: "Terms of Service",
        lastUpdated: "January 2024",
        sections: [
          {
            title: "Acceptance of Terms",
            content: "By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement."
          },
          {
            title: "Services Description",
            content: "MedQuore provides consulting services in quality management, regulatory affairs, and clinical affairs for medical device and IVD companies. All services are subject to separate service agreements."
          },
          {
            title: "Intellectual Property",
            content: "The content, organization, graphics, design, and other matters related to this website are protected under applicable copyrights and other proprietary laws."
          },
          {
            title: "Disclaimer",
            content: "The information on this website is provided on an 'as is' basis. MedQuore disclaims all warranties, express or implied, including but not limited to implied warranties of merchantability and fitness for a particular purpose."
          },
          {
            title: "Limitation of Liability",
            content: "MedQuore shall not be liable for any direct, indirect, incidental, special, or consequential damages resulting from the use or inability to use this website or services."
          }
        ]
      },
      cookies: {
        title: "Cookie Policy",
        lastUpdated: "January 2024",
        sections: [
          {
            title: "What Are Cookies",
            content: "Cookies are small text files that are stored on your computer or mobile device when you visit our website. They help us provide you with a better experience by remembering your preferences and analyzing how our website is used."
          },
          {
            title: "Types of Cookies We Use",
            content: "We use essential cookies for website functionality, analytics cookies to understand how visitors use our site (Google Analytics), and marketing cookies for LinkedIn Insight Tag to measure the effectiveness of our marketing campaigns."
          },
          {
            title: "Managing Cookies",
            content: "You can control and manage cookies through your browser settings. However, disabling certain cookies may affect the functionality of our website."
          },
          {
            title: "Third-Party Cookies",
            content: "We use Google Analytics and LinkedIn Insight Tag, which may place cookies on your device. These services have their own privacy policies governing their use of cookies."
          }
        ]
      }
    }
  },
  es: {
    nav: {
      home: "Inicio",
      services: {
        title: "Servicios",
        submenu: [
          { id: "quality-assurance", title: "Aseguramiento de Calidad", href: "/servicios/aseguramiento-calidad" },
          { id: "supplier-audit", title: "Soporte de Auditorías Globales", href: "/servicios/auditorias-globales" },
          { id: "mock-audits", title: "Auditorías e Inspecciones Simuladas", href: "/servicios/auditorias-simuladas" },
          { id: "gap-assessments", title: "Evaluaciones de Brechas", href: "/servicios/evaluaciones-brechas" },
          { id: "training-leadership", title: "Capacitación y Liderazgo", href: "/servicios/capacitacion-liderazgo" },
          { id: "talent-acquisition", title: "Adquisición de Talento y Mercado", href: "/servicios/adquisicion-talento" }
        ]
      },
      about: {
        title: "Acerca de",
        submenu: [
          { id: "company", title: "Nuestra Empresa", href: "/acerca/empresa" },
          { id: "team", title: "Equipo Directivo", href: "/acerca/equipo" },
          { id: "values", title: "Valores y Misión", href: "/acerca/valores" }
        ]
      },
      insights: {
        title: "Perspectivas",
        submenu: [
          { id: "blog", title: "Blog", href: "/perspectivas/blog" },
          { id: "resources", title: "Recursos", href: "/perspectivas/recursos" },
          { id: "newsletter", title: "Boletín", href: "/perspectivas/boletin" }
        ]
      },
      contact: "Contacto",
      bookConsultation: "Reservar Consulta"
    },
    hero: {
      headline: "Excelencia en Calidad y Regulatorio para Dispositivos Médicos e IVDs",
      subheading: "Diseñar, implementar y auditar SGC listos para auditoría en EE.UU., UE y MENA.",
      bullets: [
        "Auditores Líderes ISO 13485 y expertos en RA",
        "FDA QMSR / EU MDR e IVDR", 
        "Desde estrategia hasta ejecución práctica"
      ],
      primaryCTA: "Reservar Consulta",
      secondaryCTA: "Descargar Portafolio"
    }
  }
};

export const companyInfo = {
  name: "MedQuore",
  email: "contact@medquore.com",
  regions: ["US", "EU", "MENA"],
  linkedin: "https://linkedin.com/company/medquore",
  calendlyUrl: "https://calendly.com/medquore/intro-30"
};